import api from './api';
import {
  Invoice,
  InvoiceItem,
  Payment,
  InvoiceStats,
  PaymentStats,
  InvoiceFormData,
  PaymentFormData,
} from '../types/invoice';

export const invoiceAPI = {
  // Invoices
  getInvoices: (params?: {
    status?: string;
    customer_id?: number;
    start_date?: string;
    end_date?: string;
    search?: string;
    page?: number;
    limit?: number;
  }) => api.get<Invoice[]>('/invoices', { params }),
  
  getInvoice: (id: number) =>
    api.get<Invoice>(`/invoices/${id}`),
  
  createInvoice: (data: InvoiceFormData) =>
    api.post<Invoice>('/invoices', data),
  
  updateInvoice: (id: number, data: Partial<InvoiceFormData>) =>
    api.put<Invoice>(`/invoices/${id}`, data),
  
  deleteInvoice: (id: number) =>
    api.delete(`/invoices/${id}`),
  
  // Invoice actions
  sendInvoice: (id: number) =>
    api.post(`/invoices/${id}/send`),
  
  cancelInvoice: (id: number) =>
    api.post(`/invoices/${id}/cancel`),
  
  // Invoice items
  addInvoiceItem: (invoiceId: number, item: Partial<InvoiceItem>) =>
    api.post<InvoiceItem>(`/invoices/${invoiceId}/items`, item),
  
  updateInvoiceItem: (invoiceId: number, itemId: number, data: Partial<InvoiceItem>) =>
    api.put(`/invoices/${invoiceId}/items/${itemId}`, data),
  
  deleteInvoiceItem: (invoiceId: number, itemId: number) =>
    api.delete(`/invoices/${invoiceId}/items/${itemId}`),
  
  // Statistics and reports
  getInvoiceStats: (period?: string) =>
    api.get<InvoiceStats>('/invoices/stats', { params: { period } }),
  
  getOverdueInvoices: () =>
    api.get<Invoice[]>('/invoices/overdue'),
  
  // Create from job card
  createInvoiceFromJobCard: (jobCardId: number) =>
    api.post<Invoice>(`/invoices/from-job-card/${jobCardId}`),
  
  // Export
  exportInvoices: () =>
    api.get('/invoices/export', {
      responseType: 'blob',
    }),
  
  // PDF Export
  exportInvoicePDF: (id: number) =>
    api.get(`/invoices/${id}/pdf`, {
      responseType: 'blob',
    }),
  
  previewInvoice: (id: number) =>
    api.get(`/invoices/${id}/preview`),
};

export const paymentAPI = {
  // Payments
  getPayments: (params?: {
    status?: string;
    payment_method?: string;
    customer_id?: number;
    invoice_id?: number;
    start_date?: string;
    end_date?: string;
    search?: string;
    page?: number;
    limit?: number;
  }) => api.get<Payment[]>('/payments', { params }),
  
  getPayment: (id: number) =>
    api.get<Payment>(`/payments/${id}`),
  
  createPayment: (data: PaymentFormData) =>
    api.post<Payment>('/payments', data),
  
  updatePayment: (id: number, data: Partial<PaymentFormData>) =>
    api.put<Payment>(`/payments/${id}`, data),
  
  deletePayment: (id: number) =>
    api.delete(`/payments/${id}`),
  
  // Payment actions
  refundPayment: (id: number, data: { amount: number; reason: string }) =>
    api.post(`/payments/${id}/refund`, data),
  
  allocatePayment: (id: number, data: { allocations: Array<{ invoice_id: number; amount: number }> }) =>
    api.post(`/payments/${id}/allocate`, data),
  
  // Customer and invoice payments
  getCustomerPayments: (customerId: number) =>
    api.get<Payment[]>(`/payments/customer/${customerId}`),
  
  getInvoicePayments: (invoiceId: number) =>
    api.get<Payment[]>(`/payments/invoice/${invoiceId}`),
  
  // Statistics
  getPaymentStats: (period?: string) =>
    api.get<PaymentStats>('/payments/stats', { params: { period } }),
};